import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CartProvider, useCart } from "./CartContext";

const AuthContext = createContext(null);

const getCSRFTokenFromCookie = () => {
  const name = "csrftoken";
  const cookies = document.cookie ? document.cookie.split(";") : [];

  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return "";
};

function AuthProviderContent({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const refreshPromiseRef = useRef(null);

  const {
    cartItems,
    setCartItems,
    cartOpen,
    setCartOpen,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    increaseCartItemQuantity,
    decreaseCartItemQuantity,
    clearCart,
    getCartCount,
    getCartSubtotal,
    getWhatsAppCheckoutMessage,
    getWhatsAppCheckoutLink,
  } = useCart();

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }, []);

  const applyAuth = useCallback((userData, newAccessToken, newRefreshToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    setUser(userData);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
  }, []);

  const login = useCallback((userData, newAccessToken, newRefreshToken) => {
    applyAuth(userData, newAccessToken, newRefreshToken);
  }, [applyAuth]);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setAccessToken("");
    setRefreshToken("");
  }, [clearAuthStorage]);

  const updateUser = useCallback((userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const updateAccessToken = useCallback((newAccessToken) => {
    localStorage.setItem("accessToken", newAccessToken);
    setAccessToken(newAccessToken);
  }, []);

  const getProfileEndpointByRole = useCallback((role) => {
    if (role === "admin" || role === "staff") {
      return "/api/gym/admin/profile/";
    }
    return "/api/gym/profile/";
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const currentRefreshToken = localStorage.getItem("refreshToken") || refreshToken;

    if (!currentRefreshToken) {
      logout();
      return null;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch("/api/gym/token/refresh/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            refresh: currentRefreshToken,
          }),
        });

        if (!response.ok) {
          logout();
          return null;
        }

        const data = await response.json();
        const newAccessToken = data.access;
        const newRefreshToken = data.refresh || currentRefreshToken;

        if (!newAccessToken) {
          logout();
          return null;
        }

        localStorage.setItem("accessToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);

        return {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        };
      } catch {
        logout();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [refreshToken, logout]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const method = String(options.method || "GET").toUpperCase();

      const makeHeaders = (token) => {
        const headers = new Headers(options.headers || {});
        const csrfToken = getCSRFTokenFromCookie();
        const isFormData = options.body instanceof FormData;

        if (!headers.has("Accept")) {
          headers.set("Accept", "application/json");
        }

        if (!isFormData && !headers.has("Content-Type") && !["GET", "HEAD"].includes(method)) {
          headers.set("Content-Type", "application/json");
        }

        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }

        if (!["GET", "HEAD", "OPTIONS", "TRACE"].includes(method) && csrfToken) {
          headers.set("X-CSRFToken", csrfToken);
        }

        return headers;
      };

      let token = localStorage.getItem("accessToken") || accessToken;

      let response = await fetch(url, {
        ...options,
        method,
        headers: makeHeaders(token),
        credentials: "include",
      });

      if (response.status !== 401) {
        return response;
      }

      const refreshed = await refreshAccessToken();

      if (!refreshed?.accessToken) {
        return response;
      }

      token = refreshed.accessToken;

      response = await fetch(url, {
        ...options,
        method,
        headers: makeHeaders(token),
        credentials: "include",
      });

      if (response.status === 401) {
        logout();
      }

      return response;
    },
    [accessToken, refreshAccessToken, logout]
  );

  const loadSession = useCallback(async () => {
    const storedUser = localStorage.getItem("user");
    const storedAccessToken = localStorage.getItem("accessToken");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (!storedUser || !storedAccessToken || !storedRefreshToken) {
      logout();
      setAuthLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      const profileEndpoint = getProfileEndpointByRole(parsedUser?.role);

      setUser(parsedUser);
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);

      const response = await fetch(profileEndpoint, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${storedAccessToken}`,
        },
        credentials: "include",
      });

      if (response.ok) {
        const profile = await response.json();
        updateUser(profile);
        setAuthLoading(false);
        return;
      }

      if (response.status === 401) {
        const refreshed = await refreshAccessToken();

        if (!refreshed?.accessToken) {
          logout();
          setAuthLoading(false);
          return;
        }

        const retryResponse = await fetch(profileEndpoint, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${refreshed.accessToken}`,
          },
          credentials: "include",
        });

        if (!retryResponse.ok) {
          logout();
          setAuthLoading(false);
          return;
        }

        const profile = await retryResponse.json();
        updateUser(profile);
        setAuthLoading(false);
        return;
      }

      logout();
      setAuthLoading(false);
    } catch {
      logout();
      setAuthLoading(false);
    }
  }, [logout, refreshAccessToken, updateUser, getProfileEndpointByRole]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const isAdmin = user?.role === "admin" || user?.role === "staff";
  const isMember = user?.role === "member";

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      authLoading,
      isAuthenticated: !!user,
      isAdmin,
      isMember,
      login,
      logout,
      updateUser,
      updateAccessToken,
      refreshAccessToken,
      authFetch,
      cartItems,
      setCartItems,
      cartOpen,
      setCartOpen,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      increaseCartItemQuantity,
      decreaseCartItemQuantity,
      clearCart,
      getCartCount,
      getCartSubtotal,
      getWhatsAppCheckoutMessage,
      getWhatsAppCheckoutLink,
    }),
    [
      user,
      accessToken,
      refreshToken,
      authLoading,
      isAdmin,
      isMember,
      login,
      logout,
      updateUser,
      updateAccessToken,
      refreshAccessToken,
      authFetch,
      cartItems,
      setCartItems,
      cartOpen,
      setCartOpen,
      addToCart,
      removeFromCart,
      updateCartItemQuantity,
      increaseCartItemQuantity,
      decreaseCartItemQuantity,
      clearCart,
      getCartCount,
      getCartSubtotal,
      getWhatsAppCheckoutMessage,
      getWhatsAppCheckoutLink,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }) {
  return (
    <CartProvider>
      <AuthProviderContent>{children}</AuthProviderContent>
    </CartProvider>
  );
}

export const AppProvider = AuthProvider;

export function useAuth() {
  return useContext(AuthContext);
}

export function useApp() {
  return useContext(AuthContext);
}

export { AuthContext };